/**
 * The pipeline's whole UI state lives in the URL, and that is the point.
 *
 * ===========================================================================
 * ONE QUERY STATE, TWO VIEWS — THE BRIEF'S ACTUAL REQUIREMENT
 * ===========================================================================
 * B4 says "Board view via a toggle over the SAME query state (URL-driven, so a
 * refresh keeps the view)". Holding `view`, `stage`, `q`, `assigned`, `page`,
 * `perPage`, `sort` and `dir` in `?…` gives that literally: the toggle writes one
 * parameter, every other filter survives it, a reload restores the screen, and a link
 * pasted into chat opens the same board somebody else was looking at.
 *
 * The alternative — component state — fails three ways that matter here: the browser
 * Back button lies after a filter change, "refresh keeps the view" is false, and the
 * Record page cannot compute "3 of 12" because it has no idea which list the operator
 * came from.
 *
 * ===========================================================================
 * AN UNPARSEABLE PARAMETER FALLS BACK; IT NEVER THROWS
 * ===========================================================================
 * A hand-edited or stale URL (`?stage=needquote`, `?page=-4`) must not blank the
 * screen. Every field is narrowed here, and an unrecognised value is replaced by the
 * default. The API takes the opposite position for the SAME parameter — it refuses an
 * unknown `stage` with 422 rather than widening the result set — and both are right:
 * the API must not silently answer a different question, while the client must not
 * turn a bad bookmark into a crash. Because this module drops the bad value instead
 * of forwarding it, the API never sees one.
 *
 * `stage` is the ONE parameter shared with the dashboard: `StageCountCards` links to
 * `/countries/:cc/inquiries?stage=<enum member>`, so the name and the vocabulary here
 * are fixed by that contract, not chosen.
 */

import { useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { isInquiryStage } from '../../domain/stages';
import type { InquiryStage } from '../../domain/stages';
import { DEFAULT_SORT, isSortColumn, isSortDir } from './sorting';
import type { SortColumn, SortDir } from './sorting';

export type InquiryView = 'table' | 'board';

/**
 * `perPage` options.
 *
 * 25 is first because `Table/Pagination` reads "25 per page ▾" (node I12:3252;6:335)
 * — the design's own default, which differs from the API's `DEFAULT_PER_PAGE` of 50.
 * The client therefore always sends `perPage` explicitly rather than relying on a
 * server default that would contradict the footer the user is reading. 200 is the
 * API's `MAX_PER_PAGE`; a larger value is silently clamped server-side, so offering
 * one would be offering a lie.
 */
export const PER_PAGE_OPTIONS = [25, 50, 100, 200] as const;
export const DEFAULT_PER_PAGE = 25;

export interface InquiryQueryState {
  view: InquiryView;
  /** `null` = the "All" tab. */
  stage: InquiryStage | null;
  /** Free text, matched against inquiry subject and party name by the API. */
  q: string;
  /** A user id, or the literal `'none'` for unassigned. `null` = no owner filter. */
  assigned: string | null;
  page: number;
  perPage: number;
  sort: SortColumn;
  dir: SortDir;
}

/*
 * WEB TIER A · WT5 — `peek` and `panel` ARE GONE, WITH THE OVERLAYS THEY NAMED.
 * `?peek=<id>` opened the Peek slide-over and `?panel=new` the New Inquiry
 * panel; the owner's ruling pair (forms as full pages + the slide-over killed
 * entirely) turned both into ROUTES — `inquiries/:inquiryId` is the peek at
 * full width, `inquiries/new` is the create page — and a route needs no
 * parameter to say it is open: the path is the state. Leaving the parameters
 * parsed "for compatibility" would keep two names for facts the URL already
 * carries, and a stale `?peek=` bookmark now simply renders the list, which is
 * the same fallback-not-crash posture every other field here takes.
 */

export const UNASSIGNED = 'none';

function readInt(raw: string | null, fallback: number, min: number, max: number): number {
  if (raw === null) return fallback;
  if (!/^\d+$/.test(raw)) return fallback;
  const value = Number.parseInt(raw, 10);
  if (!Number.isSafeInteger(value) || value < min || value > max) return fallback;
  return value;
}

export function parseInquiryQuery(params: URLSearchParams): InquiryQueryState {
  const rawStage = params.get('stage');
  const rawAssigned = params.get('assigned')?.trim() ?? '';
  const perPage = readInt(params.get('perPage'), DEFAULT_PER_PAGE, 1, 200);

  const sortRaw = params.get('sort');
  const dirRaw = params.get('dir');
  const sort: SortColumn = isSortColumn(sortRaw) ? sortRaw : DEFAULT_SORT.column;
  const dir: SortDir = isSortDir(dirRaw) ? dirRaw : DEFAULT_SORT.dir;

  return {
    view: params.get('view') === 'board' ? 'board' : 'table',
    stage: isInquiryStage(rawStage) ? rawStage : null,
    q: params.get('q') ?? '',
    assigned: rawAssigned === '' ? null : rawAssigned,
    page: readInt(params.get('page'), 1, 1, 1_000_000),
    perPage,
    sort,
    dir,
  };
}

/**
 * Serialise back to `?…`, omitting every default.
 *
 * Omitting defaults is not cosmetic: it is what makes the dashboard's
 * `?stage=need_quote` link and this module's output the SAME url, so arriving from a
 * stage-count card and clicking that tab do not produce two history entries that look
 * different but render identically.
 */
export function toSearchParams(state: InquiryQueryState): URLSearchParams {
  const params = new URLSearchParams();
  if (state.view !== 'table') params.set('view', state.view);
  if (state.stage !== null) params.set('stage', state.stage);
  if (state.q !== '') params.set('q', state.q);
  if (state.assigned !== null) params.set('assigned', state.assigned);
  if (state.page !== 1) params.set('page', String(state.page));
  if (state.perPage !== DEFAULT_PER_PAGE) params.set('perPage', String(state.perPage));
  if (state.sort !== DEFAULT_SORT.column) params.set('sort', state.sort);
  if (state.dir !== DEFAULT_SORT.dir) params.set('dir', state.dir);
  return params;
}

/**
 * Which fields, when changed, must reset `page` to 1.
 *
 * Without this, filtering while on page 3 asks the API for rows 51–75 of a result set
 * that now has 4 rows and renders an empty table — the classic "my filter broke the
 * screen" bug. `sort` is NOT in the list: sorting is applied client-side within the
 * page (see `sorting.ts`), so it cannot change which rows the page contains.
 */
const PAGE_RESETTING: readonly (keyof InquiryQueryState)[] = ['stage', 'q', 'assigned', 'perPage'];

export function nextQueryState(
  current: InquiryQueryState,
  patch: Partial<InquiryQueryState>,
): InquiryQueryState {
  const merged: InquiryQueryState = { ...current, ...patch };
  const resets = PAGE_RESETTING.some(
    (key) => patch[key] !== undefined && patch[key] !== current[key],
  );
  if (resets && patch.page === undefined) merged.page = 1;
  return merged;
}

export interface InquiryQueryApi {
  state: InquiryQueryState;
  /** Merge a patch into the URL. `replace` avoids a history entry (typing in search). */
  update: (patch: Partial<InquiryQueryState>, options?: { replace?: boolean }) => void;
  /** The `?…` string for a sibling route (the Record page keeps the list context). */
  search: string;
}

export function useInquiryQuery(): InquiryQueryApi {
  const [params, setParams] = useSearchParams();

  const state = useMemo(() => parseInquiryQuery(params), [params]);

  const update = useCallback(
    (patch: Partial<InquiryQueryState>, options?: { replace?: boolean }): void => {
      setParams(toSearchParams(nextQueryState(parseInquiryQuery(params), patch)), {
        replace: options?.replace ?? false,
      });
    },
    [params, setParams],
  );

  const search = useMemo(() => {
    const qs = toSearchParams(state).toString();
    return qs === '' ? '' : `?${qs}`;
  }, [state]);

  return { state, update, search };
}

/**
 * The parameters that actually go to `GET /countries/:cc/inquiries`.
 *
 * `sort`/`dir`/`view` are deliberately NOT among them: the API's
 * inquiry filters are `stage`, `assigned`, `q`, `page`, `perPage` and nothing else
 * (`InquiryListQuery`). Sending `?sort=` would be sending a parameter the server
 * ignores while the UI implied it obeyed.
 */
export function listParamsFor(state: InquiryQueryState): {
  stage?: string;
  assigned?: string;
  q?: string;
  page: number;
  perPage: number;
} {
  return {
    ...(state.stage === null ? {} : { stage: state.stage }),
    ...(state.assigned === null ? {} : { assigned: state.assigned }),
    ...(state.q.trim() === '' ? {} : { q: state.q.trim() }),
    page: state.page,
    perPage: state.perPage,
  };
}
